type BuilderRoot = Document | ShadowRoot;

/**
 * Get the builder's shadow root. For tests and stories where shadow is
 * bypassed, the window's document is returned instead
 */
export const getBuilderRoot = (): BuilderRoot | null => {
  const builderTagName = `appcues-builder${
    import.meta.env.STANDALONE ? '-standalone' : ''
  }`;
  const $builder = document.querySelector(builderTagName);
  if (import.meta.env.SHADOW_BYPASS) {
    return document;
  }
  return $builder?.shadowRoot ?? null;
};

/**
 * Get the builder custom element
 */
export const getBuilderElement = (): Document | Element | null => {
  const builderTagName = `appcues-builder${
    import.meta.env.STANDALONE ? '-standalone' : ''
  }`;
  const $builder = document.querySelector(builderTagName);
  return import.meta.env.SHADOW_BYPASS ? document : $builder;
};

/**
 * Get the builder custom element as an `HTMLElement` for positioning consumers
 * (e.g. Floating UI's `parentEl`). Returns `null` when the element is missing
 * or when running under `SHADOW_BYPASS` (where `getBuilderElement` returns the
 * `Document`, which can't anchor a floating panel).
 */
export const getBuilderHtmlElement = (): HTMLElement | null => {
  const $builder = getBuilderElement();
  return $builder instanceof HTMLElement ? $builder : null;
};

/**
 * Get the portal's root element within the builder.
 * Portal roots are always HTMLElements (div containers).
 */
export const getPortalRoot = (
  $builder: BuilderRoot | null = getBuilderRoot()
): HTMLElement | null => {
  return $builder?.querySelector<HTMLElement>('#portal-root') ?? null;
};

/**
 * Get the editor's document object nested within shadow roots and iframes
 */
export const getEditorDocument = (
  $builder: BuilderRoot | null = getBuilderRoot()
): Document | null => {
  if (!$builder) {
    return null;
  }

  const $editor = $builder.querySelector('#editor');
  if (!$editor?.shadowRoot) {
    return null;
  }

  const $frame = $editor.shadowRoot.querySelector('iframe');
  if (!$frame) {
    return null;
  }

  return $frame.contentWindow?.document ?? null;
};
